import React from "react";
import clsx from "clsx";

import { Button, IconChevronLeft, IconChevronRight } from "@bared/ui";

type PaginationProps = {
  /** Current Page */
  page: number;
  /** Total number of products */
  totalProducts: number;
  /** Page size */
  size: number;
  /** On change */
  onPageChange: (page: number) => void;
};

export const Pagination = ({
  page,
  totalProducts,
  size,
  onPageChange,
}: PaginationProps) => {
  const totalPages = Math.max(1, Math.ceil(totalProducts / size));

  const handlePageChange = (page: number) => {
    onPageChange(Math.min(Math.max(1, page), totalPages));
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <nav
      aria-label="Pagination"
      className="mb-[40px] mt-midi flex w-full justify-end"
    >
      {page > 1 && (
        <Button
          variant="plain"
          onClick={() => handlePageChange(page - 1)}
          aria-label="Previous Page"
          className="[> aria-disabled]:text-grey [> aria-disabled]:cursor-default flex h-maxi w-maxi items-center justify-center"
        >
          <IconChevronLeft className="h-mini w-mini" />
        </Button>
      )}

      {Array.from({ length: totalPages }, (_, i) => i + 1).map(
        (currentPage) => (
          <Button
            variant="plain"
            key={currentPage}
            onClick={() => handlePageChange(currentPage)}
            aria-current={currentPage === page ? "page" : undefined}
            className={clsx(
              "border-grey mx-pico flex h-maxi w-maxi items-center justify-center border text-body text-primary",
              currentPage === page
                ? "border-none bg-primary text-white"
                : "bg-transparent hover:border-none hover:bg-tertiary",
            )}
          >
            {currentPage}
          </Button>
        ),
      )}

      <Button
        variant="plain"
        disabled={page >= totalPages}
        onClick={() => handlePageChange(page + 1)}
        aria-label="Next Page"
        className="[> aria-disabled]:text-grey [> aria-disabled]:cursor-default flex h-maxi w-maxi items-center justify-center"
      >
        <IconChevronRight className="h-mini w-mini" />
      </Button>
    </nav>
  );
};
